import type { Country } from './Country.js';
import type { CountryCode } from './CountryCode.js';

/**
 * Minimal record shape from persistence (Amplify/DynamoDB or similar).
 * Keeps shared-domain free of infrastructure dependencies.
 */
export type CountryRecord = {
  code: string;
  code3?: string | null;
  numericCode?: string | null;
  name: string;
  nameEn?: string | null;
  nameEl?: string | null;
  nameRu?: string | null;
  nameOfficial?: string | null;
  softoneCountryCode?: string | null;
  softoneCountryShortcut?: string | null;
  softoneCountryName?: string | null;
  softoneCountryCurrency?: string | null;
  softoneCountryType?: string | null;
  softoneCountryIsActive?: boolean | null;
  softoneCountryAcnmsk?: string | null;
  softoneCountryEanCode?: string | null;
  softoneCountryOssDVat?: string | null;
  softoneCountryOssAVat?: string | null;
  softoneSyncedAt?: string | null;
  softoneSyncResult?: string | null;
  continent?: string | null;
  region?: string | null;
  subregion?: string | null;
  capitalCity?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  iataCode?: string | null;
  icaoCode?: string | null;
  iataZone?: string | null;
  isEU?: boolean | null;
  isSchengen?: boolean | null;
  isEurozone?: boolean | null;
  marketZone?: string | null;
  phonePrefixes?: string[] | null;
  currencyCode?: string | null;
  currencyName?: string | null;
  currencySymbol?: string | null;
  languages?: string[] | null;
  tld?: string | null;
  postalCodeFormat?: string | null;
  postalCodeRegex?: string | null;
  flag?: string | null;
  notes?: string | null;
  isActive?: boolean | null;
  lastSyncedAt?: string | null;
  syncSource?: string | null;
  timezone?: string | null;
  gmtOffset?: number | null;
  isdst?: boolean | null;
};

/**
 * Maps persistence record to domain Country.
 */
export function toDomain(record: CountryRecord): Country {
  return {
    code: record.code as CountryCode,
    code3: record.code3 ?? '',
    numericCode: record.numericCode ?? '',
    name: record.name,
    nameEn: record.nameEn ?? record.name,
    nameEl: record.nameEl ?? '',
    nameRu: record.nameRu ?? '',
    nameOfficial: record.nameOfficial ?? record.name,
    softoneCountryCode: record.softoneCountryCode ?? undefined,
    softoneCountryShortcut: record.softoneCountryShortcut ?? undefined,
    softoneCountryName: record.softoneCountryName ?? undefined,
    softoneCountryCurrency: record.softoneCountryCurrency ?? undefined,
    softoneCountryType: record.softoneCountryType ?? undefined,
    softoneCountryIsActive: record.softoneCountryIsActive ?? undefined,
    softoneCountryAcnmsk: record.softoneCountryAcnmsk ?? undefined,
    softoneCountryEanCode: record.softoneCountryEanCode ?? undefined,
    softoneCountryOssDVat: record.softoneCountryOssDVat ?? undefined,
    softoneCountryOssAVat: record.softoneCountryOssAVat ?? undefined,
    softoneSyncedAt: record.softoneSyncedAt ? new Date(record.softoneSyncedAt) : undefined,
    softoneSyncResult: record.softoneSyncResult ?? undefined,
    continent: record.continent ?? '',
    region: record.region ?? '',
    subregion: record.subregion ?? '',
    capitalCity: record.capitalCity ?? '',
    latitude: record.latitude ?? 0,
    longitude: record.longitude ?? 0,
    iataCode: record.iataCode ?? undefined,
    icaoCode: record.icaoCode ?? undefined,
    iataZone: record.iataZone ?? undefined,
    isEU: record.isEU ?? false,
    isSchengen: record.isSchengen ?? false,
    isEurozone: record.isEurozone ?? false,
    marketZone: record.marketZone ?? undefined,
    phonePrefixes: record.phonePrefixes ?? [],
    currencyCode: record.currencyCode ?? '',
    currencyName: record.currencyName ?? '',
    currencySymbol: record.currencySymbol ?? '',
    languages: record.languages ?? [],
    tld: record.tld ?? '',
    postalCodeFormat: record.postalCodeFormat ?? undefined,
    postalCodeRegex: record.postalCodeRegex ?? undefined,
    flag: record.flag ?? '',
    notes: record.notes ?? undefined,
    isActive: record.isActive ?? true,
    lastSyncedAt: record.lastSyncedAt ? new Date(record.lastSyncedAt) : new Date(0),
    syncSource: record.syncSource ?? '',
    timezone: record.timezone ?? undefined,
    gmtOffset: record.gmtOffset ?? undefined,
    isdst: record.isdst ?? undefined,
  };
}

/**
 * Returns display-friendly shape for UI (code, name, dialCode).
 */
export function getCountryDisplay(country: Country): {
  code: string;
  name: string;
  dialCode: string;
} {
  const dialCode = country.phonePrefixes?.[0] ?? '';
  return {
    code: country.code,
    name: country.name,
    dialCode,
  };
}
